import { createSlice } from '@reduxjs/toolkit';

import { logout } from './auth';

// IMPORTANT: MFA tokens, the otpauth secret, and recovery codes are
// short-lived secrets kept in memory only; never write them to localStorage.
const initialState = {
  loading: false,
  error: null,
  enrollmentToken: null,
  mfaToken: null,
  enrollmentRequired: false,
  mfaRequired: false,
  recoveryCodesRemaining: null,
  enrollment: null,
  recoveryCodes: null,
};

const mfaSlice = createSlice({
  name: 'mfa',
  initialState,
  reducers: {
    setEnrollmentRequired: (state, action) => {
      state.enrollmentRequired = true;
      state.mfaRequired = false;
      state.enrollmentToken = action.payload.enrollmentToken;
      state.mfaToken = null;
      state.error = null;
    },
    setMFARequired: (state, action) => {
      state.mfaRequired = true;
      state.enrollmentRequired = false;
      state.mfaToken = action.payload.mfaToken;
      state.recoveryCodesRemaining = action.payload.recoveryCodesRemaining ?? null;
      state.enrollmentToken = null;
      state.error = null;
    },
    enrollStartRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    enrollStartSuccess: (state, action) => {
      state.loading = false;
      state.enrollment = action.payload;
    },
    enrollStartFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    enrollConfirmRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    enrollConfirmSuccess: (state, action) => {
      state.loading = false;
      state.recoveryCodes = action.payload;
      state.enrollment = null;
      state.enrollmentToken = null;
      state.enrollmentRequired = false;
    },
    enrollConfirmFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    verifyRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    verifyFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    recoveryRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    recoveryFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    dismissRecoveryCodes: (state) => {
      state.recoveryCodes = null;
    },
    // After a successful MFA-path login: drop scoped tokens so they can't
    // be replayed from memory, but keep recoveryCodes so the post-enrollment
    // download modal stays open until the user dismisses it.
    clearScopedTokens: (state) => {
      state.enrollmentToken = null;
      state.mfaToken = null;
      state.enrollmentRequired = false;
      state.mfaRequired = false;
      state.enrollment = null;
      state.recoveryCodesRemaining = null;
      state.error = null;
      state.loading = false;
    },
    clearMFA: () => initialState,
  },
  // Logging out wipes MFA state too, preventing one user's leftover tokens
  // or recovery codes from being visible to the next user on this browser.
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const {
  setEnrollmentRequired,
  setMFARequired,
  enrollStartRequest,
  enrollStartSuccess,
  enrollStartFailure,
  enrollConfirmRequest,
  enrollConfirmSuccess,
  enrollConfirmFailure,
  verifyRequest,
  verifyFailure,
  recoveryRequest,
  recoveryFailure,
  dismissRecoveryCodes,
  clearScopedTokens,
  clearMFA,
} = mfaSlice.actions;
export { mfaSlice };
